import { Fragment, useRef, useState } from 'react';

import Slider from 'react-slick';

import map from 'lodash/fp/map';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import type { StaticImageData } from 'Components/Image';
import { SliderArrow } from 'Components/SliderArrow';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { IngredientCard } from './IngredientCard';

export type SliderCardsContent = {
  ingredientName: string;
  description: string;
  img: { imgSrc: StaticImageData; imgAlt: string };
}[];

const SliderContainer = styled.div`
  position: relative;
`;

const StyledSlider = styled(Slider)<{ slideNumber: number }>`
  width: 100%;
  ${legacyTheme.breakpoints.up('sm')} {
    /* TODO: @nelly cleanup if possible this is a quick layout fix
       fixes centering on tablet */
    width: 98%;
  }
  ${legacyTheme.breakpoints.up('md')} {
    /* TODO: @nelly cleanup if possible this is a quick layout fix
       fixes centering on desktop */
    width: 100%;
    margin-left: -10px;
  }

  .slick-track .slick-slide:last-child {
    padding-right: 12px;
  }

  .slick-prev,
  .slick-next {
    width: 10px;
    height: auto;
  }

  .slick-prev::before,
  .slick-next::before {
    display: none;
  }
`;

const SliderCardsContainer = styled.div`
  padding: 0 0 0 ${theme.spacing['6x']};
`;

type IngredientsSliderProps = { cardsContent: SliderCardsContent; trackOnChange?: () => void };

export const IngredientsSlider = ({ cardsContent, trackOnChange }: IngredientsSliderProps) => {
  const { isMobile, isTablet, isDesktop } = useResponsiveVariant();
  const [displayArrows, setDisplayArrows] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef<Slider>(null);
  const slideToShowDesktop = 6;
  const slidesToShowValue: number = isMobile ? 1.6 : isTablet ? 4 : slideToShowDesktop;

  const showNextsArrow = currentSlide !== cardsContent.length - slideToShowDesktop;
  const showPreviousArrow = currentSlide > 0;

  const sliderArrowProps = { leftAndRight: 16, top: 45 };

  const cards = map((ingredient) => {
    return (
      <Fragment key={ingredient.ingredientName}>
        <SliderCardsContainer>
          <IngredientCard ingredient={ingredient} />
        </SliderCardsContainer>
      </Fragment>
    );
  }, cardsContent);

  return (
    <SliderContainer
      onMouseEnter={() => setDisplayArrows(true)}
      onMouseLeave={() => setDisplayArrows(false)}
    >
      {isDesktop && showPreviousArrow && displayArrows && (
        <SliderArrow
          dataFrom="ingredients-module-carousel"
          direction="prev"
          leftAndRight={-sliderArrowProps.leftAndRight}
          onClick={() => sliderRef?.current?.slickPrev()}
          top={sliderArrowProps.top}
        />
      )}

      <StyledSlider
        ref={sliderRef}
        afterChange={(slide: number) => {
          trackOnChange?.();
          setCurrentSlide(Math.round(slide));
        }}
        arrows={false}
        dots={false}
        focusOnSelect
        infinite={false}
        slideNumber={cards.length - 1}
        slidesToShow={slidesToShowValue}
      >
        {cards}
      </StyledSlider>

      {isDesktop && showNextsArrow && displayArrows && (
        <SliderArrow
          dataFrom="ingredients-module-carousel"
          direction="next"
          leftAndRight={-sliderArrowProps.leftAndRight}
          onClick={() => sliderRef?.current?.slickNext()}
          top={sliderArrowProps.top}
        />
      )}
    </SliderContainer>
  );
};
